import { LIVE_FEED_AUTH_FAIL_CODES, LIVE_FEED_PATH, type LiveFeedEnvelope } from '~/types/live-feed'
import { useUserStore } from '~/stores/user'
import { probeSession } from '~/utils/apiInterceptors'

const RECONNECT_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 16_000, 30_000] as const
const AUTHENTICATED_STATE_KEY = 'live-feed-authenticated'

const eventListeners = new Set<(message: LiveFeedEnvelope) => void>()
const reconnectListeners = new Set<() => void>()

let started = false
let socket: WebSocket | null = null
let reconnectTimer: ReturnType<typeof setTimeout> | null = null
let reconnectAttempt = 0
let everAuthenticated = false
let authenticated = false
let authenticatedRef: Ref<boolean> | null = null
let userStore: ReturnType<typeof useUserStore> | null = null

/**
 * 实时推送地址：
 * - `NUXT_PUBLIC_LIVE_FEED_URL` 设了就用它（主机部分，代码拼 `/ws/live-feed`）；设成 `off` 则不连。
 * - 没设：构建产物连当前页面同源的 `/ws/live-feed`（测试服 / 生产由 nginx 把 `/ws/` 转给 game-api）。
 *   不再按构建写死主机——同一份构建要服务所有租户域名，地址只能在运行时按当前域名算；
 *   写死成另一个主机还会在 https 页面上变成混合内容被浏览器拦下。
 * - 没设且是 `npm run dev`：不连。Nuxt 开发服务器不代理 WebSocket 升级请求，同源连不上；
 *   本地要看实时推送就在 shell 里设 `NUXT_PUBLIC_LIVE_FEED_URL=ws://<game-api 主机>/` 再起。
 */
function liveFeedUrl() {
  const base = String(import.meta.env.NUXT_PUBLIC_LIVE_FEED_URL || '')
    .trim()
    .replace(/\/+$/, '')
  if (base === 'off') return ''
  if (base) return `${base}${LIVE_FEED_PATH}`
  if (import.meta.dev || !import.meta.client) return ''
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}${LIVE_FEED_PATH}`
}

function parseEnvelope(raw: string): LiveFeedEnvelope | null {
  try {
    const value: unknown = JSON.parse(raw)
    if (!value || typeof value !== 'object') return null
    if (!('event' in value) || typeof (value as LiveFeedEnvelope).event !== 'string') return null
    return value as LiveFeedEnvelope
  } catch {
    return null
  }
}

function setAuthenticated(value: boolean) {
  authenticated = value
  if (authenticatedRef) authenticatedRef.value = value
}

function getToken() {
  return userStore?.token ?? ''
}

function shouldConnect() {
  return Boolean(getToken() && liveFeedUrl())
}

function clearReconnect() {
  if (!reconnectTimer) return
  clearTimeout(reconnectTimer)
  reconnectTimer = null
}

function dropSocket() {
  if (!socket) return
  socket.onopen = null
  socket.onmessage = null
  socket.onerror = null
  socket.onclose = null
  if (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING) {
    socket.close()
  }
  socket = null
  setAuthenticated(false)
}

function disconnect(resetSession = false) {
  clearReconnect()
  dropSocket()
  if (resetSession) {
    everAuthenticated = false
    reconnectAttempt = 0
  }
}

function scheduleReconnect() {
  if (!shouldConnect() || reconnectTimer) return
  const delay =
    RECONNECT_DELAYS_MS[Math.min(reconnectAttempt, RECONNECT_DELAYS_MS.length - 1)] ?? 30_000
  reconnectAttempt += 1
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null
    if (shouldConnect()) connect()
  }, delay)
}

function connect() {
  const url = liveFeedUrl()
  const token = getToken()
  if (!url || !token || socket) return

  let ws: WebSocket
  try {
    ws = new WebSocket(url)
  } catch (err) {
    if (import.meta.dev) {
      console.warn('[live-feed] failed to construct socket:', err)
    }
    return
  }
  socket = ws

  ws.onopen = () => {
    if (ws !== socket) return
    const nextToken = getToken()
    if (!nextToken) {
      disconnect(true)
      return
    }
    ws.send(JSON.stringify({ event: 'auth', data: { token: nextToken } }))
  }

  ws.onmessage = event => {
    if (ws !== socket) return
    const message = typeof event.data === 'string' ? parseEnvelope(event.data) : null
    if (!message) return

    if (message.event === 'auth:ok') {
      setAuthenticated(true)
      reconnectAttempt = 0
      if (everAuthenticated) {
        reconnectListeners.forEach(listener => listener())
      }
      everAuthenticated = true
      return
    }

    if (!authenticated) return
    eventListeners.forEach(listener => listener(message))
  }

  ws.onerror = () => {
    if (import.meta.dev) {
      console.warn('[live-feed] socket error')
    }
  }

  ws.onclose = ev => {
    if (ws !== socket) return
    socket = null
    setAuthenticated(false)

    if (LIVE_FEED_AUTH_FAIL_CODES.has(ev.code)) {
      const failedToken = getToken()
      console.info('[auth] live-feed auth close:', ev.code, '→ 先探活再决定是否清会话')
      if (!failedToken) return // 会话已不在（别处已清），无需再判
      // 与 HTTP 401 同理：弱网 / 后端会话传播延迟会让新 token 在 WS 侧被拒，
      // HTTP 探活确认仍有效就当瞬时失败退避重连，不得清登录态
      void (async () => {
        const alive = await probeSession(failedToken)
        if (alive || userStore?.token !== failedToken) {
          scheduleReconnect()
          return
        }
        console.warn('[auth] live-feed confirmed dead session, clearing')
        userStore?.expireOnUnauthorized()
        everAuthenticated = false
      })()
      return
    }

    if (shouldConnect()) scheduleReconnect()
  }
}

/** 登录后全站共用一条原生 WS；业务方只订阅，不各自建连 */
export function startLiveFeed() {
  if (!import.meta.client || started) return
  started = true

  userStore = useUserStore()
  authenticatedRef = useState(AUTHENTICATED_STATE_KEY, () => false)

  watch(
    [() => getToken(), liveFeedUrl],
    ([token, url]) => {
      disconnect(true)
      if (token && url) connect()
    },
    { immediate: true },
  )

  window.addEventListener(
    'pagehide',
    () => {
      disconnect(true)
    },
    { once: true },
  )
}

/** 订阅全局 live-feed。离开页面只取消订阅，不断开连接 */
export function useLiveFeed() {
  const state = useState(AUTHENTICATED_STATE_KEY, () => false)
  if (import.meta.client) startLiveFeed()

  const subscribe = (handler: (message: LiveFeedEnvelope) => void) => {
    eventListeners.add(handler)
    onUnmounted(() => eventListeners.delete(handler))
  }

  const onReconnected = (handler: () => void) => {
    reconnectListeners.add(handler)
    onUnmounted(() => reconnectListeners.delete(handler))
  }

  return { authenticated: state, subscribe, onReconnected }
}
